import { useEffect, useRef, useCallback, useState } from "react";
import { getWsBaseUrl } from "../../utils/domainConfig";

// wss://<host>/ws/ludo — sibling of /ws/casino, NOT under /api like REST endpoints.
function getLudoWsUrl() {
  return `${getWsBaseUrl()}/ws/ludo`;
}

// Admin/spectator join: identifies the room by its human-readable roomCode
// (e.g. "LUDOA1B2C3" — the "LUDO" prefix is optional, the backend strips it)
// instead of the raw gameId/roomId a real player uses. Read-only: never
// sends roll_dice / move_piece — the backend rejects those from an
// unassigned (non-player) connection anyway ("Not your turn").
export function useLudoSocket({ roomCode, onMessage }) {
  const wsRef = useRef(null);
  const onMsgRef = useRef(onMessage);
  onMsgRef.current = onMessage;

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!roomCode) return;
    let destroyed = false;
    let retryTimer;

    function connect() {
      if (destroyed) return;
      const ws = new WebSocket(getLudoWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        if (destroyed) { ws.close(); return; }
        setConnected(true);
        setReconnecting(false);
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "connected") {
            ws.send(JSON.stringify({ type: "join", token, roomCode }));
          }
          onMsgRef.current?.(msg);
        } catch (_) { /* ignore */ }
      };

      ws.onclose = () => {
        if (destroyed) return;
        setConnected(false);
        setReconnecting(true);
        retryTimer = setTimeout(connect, 2000);
      };

      ws.onerror = () => ws.close();
    }

    connect();

    return () => {
      destroyed = true;
      clearTimeout(retryTimer);
      wsRef.current?.close();
    };
  }, [roomCode, token]);

  return { connected, reconnecting };
}
